import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw, Info } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Tooltip } from '../../../../components/ui/Tooltip';
import { LABEL_STANDARD } from '../../../../lib/formStyles';

const SQRT3 = Math.sqrt(3);

const TABS = [
  { id: 'single', label: 'Single Phase', title: 'WAV Single Phase' },
  { id: 'three', label: 'Three Phase', title: 'WAV Three Phase' },
];

const initialValues = {
  single: { watts: '', amps: '', volts: '' },
  three: { kw: '', amps: '', volts: '', pf: '' },
};

function InputRow({ label, placeholder, value, onChange, infoTooltip }) {
  return (
    <div>
      <label className={LABEL_STANDARD} htmlFor={label}>
        {label}
        {infoTooltip && (
          <Tooltip content={infoTooltip}>
            <button
              type="button"
              className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
              aria-label="Information"
            >
              <Info className="h-3 w-3" />
            </button>
          </Tooltip>
        )}
      </label>
      <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
        <input
          id={label}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
          aria-label={label}
        />
      </div>
    </div>
  );
}

export default function WattAmpsVoltsCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [activeTab, setActiveTab] = useState('single');
  const [values, setValues] = useState(initialValues);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateValues = (tab, field, val) => {
    setValues((p) => ({
      ...p,
      [tab]: { ...p[tab], [field]: val },
    }));
  };

  const countFilled = (arr) =>
    arr.filter((v) => v !== '' && v !== null && v !== undefined).length;

  const handleCalculate = () => {
    if (activeTab === 'single') {
      const { watts, amps, volts } = values.single;
      const p = parseFloat(watts);
      const i = parseFloat(amps);
      const v = parseFloat(volts);
      const filled = countFilled([watts, amps, volts]);
      if (filled !== 2) return;

      let newWatts = watts !== '' ? p : null;
      let newAmps = amps !== '' ? i : null;
      let newVolts = volts !== '' ? v : null;

      if (newWatts != null && newAmps != null) {
        newVolts = newWatts / newAmps;
      } else if (newWatts != null && newVolts != null) {
        newAmps = newWatts / newVolts;
      } else if (newAmps != null && newVolts != null) {
        newWatts = newAmps * newVolts;
      }

      setValues((prev) => ({
        ...prev,
        single: {
          watts: newWatts != null ? String(Number(newWatts.toFixed(4))) : '',
          amps: newAmps != null ? String(Number(newAmps.toFixed(4))) : '',
          volts: newVolts != null ? String(Number(newVolts.toFixed(4))) : '',
        },
      }));
    } else {
      // Three phase: kW = (√3 × V × I × PF) / 1000
      const { kw, amps, volts, pf } = values.three;
      let kwVal = parseFloat(kw);
      const i = parseFloat(amps);
      const v = parseFloat(volts);
      let pfVal = parseFloat(pf);
      if (pfVal > 1) pfVal = pfVal / 100;
      const filled = countFilled([kw, amps, volts, pf]);
      if (filled !== 3) return;

      let newKw = kw !== '' ? kwVal : null;
      let newAmps = amps !== '' ? i : null;
      let newVolts = volts !== '' ? v : null;
      let newPf = pf !== '' ? pfVal : null;

      const denom = SQRT3 * 1000;
      if (newKw != null && newAmps != null && newVolts != null) {
        newPf = (newKw * 1000) / (SQRT3 * newVolts * newAmps);
      } else if (newKw != null && newAmps != null && newPf != null && newPf > 0) {
        newVolts = (newKw * 1000) / (SQRT3 * newAmps * newPf);
      } else if (newKw != null && newVolts != null && newPf != null && newPf > 0) {
        newAmps = (newKw * 1000) / (SQRT3 * newVolts * newPf);
      } else if (newAmps != null && newVolts != null && newPf != null) {
        newKw = (SQRT3 * newVolts * newAmps * newPf) / 1000;
      }

      setValues((prev) => ({
        ...prev,
        three: {
          kw: newKw != null ? String(Number(newKw.toFixed(4))) : '',
          amps: newAmps != null ? String(Number(newAmps.toFixed(4))) : '',
          volts: newVolts != null ? String(Number(newVolts.toFixed(4))) : '',
          pf: newPf != null ? String(Number(newPf.toFixed(4))) : '',
        },
      }));
    }
  };

  const handleReset = () => {
    setValues(JSON.parse(JSON.stringify(initialValues)));
  };

  const vs = values.single;
  const vt = values.three;

  const canCalcSingle = countFilled([vs.watts, vs.amps, vs.volts]) === 2;
  const canCalcThree = countFilled([vt.kw, vt.amps, vt.volts, vt.pf]) === 3;

  const canCalculate =
    (activeTab === 'single' && canCalcSingle) ||
    (activeTab === 'three' && canCalcThree);

  const currentTab = TABS.find((t) => t.id === activeTab);
  const instruction =
    activeTab === 'single'
      ? 'Enter any two values to calculate the third'
      : 'Enter any three values to calculate the fourth';

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">{currentTab?.title}</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
        {instruction}
      </p>

      <div className={`space-y-4 ${isMobile ? 'pb-24' : ''}`}>
        {activeTab === 'single' && (
          <>
            <InputRow
              label="Watts"
              placeholder="Wattage"
              value={vs.watts}
              onChange={(val) => updateValues('single', 'watts', val)}
            />
            <InputRow
              label="Amps"
              placeholder="Amperes"
              value={vs.amps}
              onChange={(val) => updateValues('single', 'amps', val)}
            />
            <InputRow
              label="Volts"
              placeholder="L-N Voltage"
              value={vs.volts}
              onChange={(val) => updateValues('single', 'volts', val)}
            />
          </>
        )}

        {activeTab === 'three' && (
          <>
            <InputRow
              label="kW"
              placeholder="Kilowatts"
              value={vt.kw}
              onChange={(val) => updateValues('three', 'kw', val)}
            />
            <InputRow
              label="Amps"
              placeholder="Amperes"
              value={vt.amps}
              onChange={(val) => updateValues('three', 'amps', val)}
            />
            <InputRow
              label="Volts"
              placeholder="L-L Voltage"
              value={vt.volts}
              onChange={(val) => updateValues('three', 'volts', val)}
            />
            <InputRow
              label="PF"
              placeholder="Power Factor"
              value={vt.pf}
              onChange={(val) => updateValues('three', 'pf', val)}
              infoTooltip="Power Factor: ratio of real power (kW) to apparent power. Typically 0.8–0.95. Enter as decimal (e.g. 0.85) or percentage (e.g. 85)."
            />
          </>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Bottom phase selector */}
      <nav
        className={`flex border-t border-gray-200 dark:border-gray-700 bg-gray-800 dark:bg-gray-900 ${
          isMobile
            ? 'fixed bottom-0 left-0 right-0 z-40'
            : 'mt-6 -mx-6 -mb-6 rounded-b-lg overflow-hidden'
        }`}
        role="tablist"
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 dark:bg-blue-500 text-white'
                  : 'text-white/80 hover:text-white hover:bg-gray-700 dark:hover:bg-gray-800'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Watt Amps Volts Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate power, current, or voltage for single-phase and three-phase systems
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
